// Stand-in for the Recover Analytics endpoints until they exist. The contract they follow is
// docs/temp/disputes-analytics-backend-tz.md, and every handler applies the query parameters the
// real endpoint will — the window, the case type, the comparison, paging, sorting, search and the
// activity filters — so the page is exercised for real against it.
//
// The disputes are generated with their history (status transitions, evidence versions,
// submissions) from a fixed seed: the same set on every load, dated relative to today.
//
// Delete this file together with the mock branches in services.js once the endpoints land.
import dayjs from "dayjs"

import {
  COMPARE_BE_VALUES,
  getCompareRange
} from "../../../Components/Filters/CompareSelect/presets"
import { STATUS_LABELS } from "../../DisputesPage/pages/AllPage/constants/statusMeta"

const ISO = "YYYY-MM-DD"
const LABEL_FORMAT = "MMM D, YYYY"
const DISPUTE_COUNT = 1600
const HISTORY_DAYS = 400

const ACTIVE = ["needs_response", "awaiting_review", "pending_decision"]
const LOST = ["lost_defended", "lost_undefended"]
const SUBMITTED = ["pending_decision", "won", "lost_defended", "escalated"]
const MATRIX_NETWORKS = ["visa", "mastercard", "amex", "discover"]

const NETWORK_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  discover: "Discover",
  other: "Other"
}

const CATEGORY_LABELS = {
  fraud: "Fraud",
  consumer_disputes: "Consumer Disputes",
  processing_errors: "Processing Errors",
  authorization_related_chargeback: "Authorization",
  unknown: "Other"
}

const REASON_CODES = {
  visa: {
    fraud: [
      ["10.4", "Other Fraud – Card-Absent Environment"],
      ["10.5", "Visa Fraud Monitoring Program"]
    ],
    consumer_disputes: [
      ["13.1", "Merchandise/Services Not Received"],
      ["13.2", "Cancelled Recurring Transaction"],
      ["13.3", "Not as Described or Defective Merchandise/Services"],
      ["13.6", "Credit Not Processed"]
    ],
    processing_errors: [
      ["12.5", "Incorrect Amount"],
      ["12.6.1", "Duplicate Processing"]
    ],
    authorization_related_chargeback: [["11.3", "No Authorization"]]
  },
  mastercard: {
    fraud: [
      ["4837", "No Cardholder Authorization"],
      ["4863", "Cardholder Does Not Recognize – Potential Fraud"]
    ],
    consumer_disputes: [
      ["4853", "Cardholder Dispute"],
      ["4855", "Goods or Services Not Provided"]
    ],
    processing_errors: [["4834", "Point-of-Interaction Error"]],
    authorization_related_chargeback: [["4808", "Authorization-Related Chargeback"]]
  },
  amex: {
    fraud: [
      ["F29", "Card Not Present"],
      ["F24", "No Card Member Authorization"]
    ],
    consumer_disputes: [
      ["C08", "Goods/Services Not Received or Only Partially Received"],
      ["C31", "Goods/Services Not as Described"]
    ],
    processing_errors: [["P08", "Duplicate Charge"]],
    authorization_related_chargeback: [["A08", "Authorization Approval Expired"]]
  },
  discover: {
    fraud: [["UA02", "Fraud – Card Not Present Transaction"]],
    consumer_disputes: [
      ["RG", "Non-Receipt of Goods, Services or Cash"],
      ["RM", "Quality Discrepancy"]
    ],
    processing_errors: [["DP", "Duplicate Processing"]],
    authorization_related_chargeback: [["AT", "Authorization Noncompliance"]]
  }
}

const NETWORK_WEIGHTS = [
  ["visa", 50],
  ["mastercard", 30],
  ["amex", 11],
  ["discover", 7],
  ["jcb", 2]
]

const CATEGORY_WEIGHTS = [
  ["fraud", 42],
  ["consumer_disputes", 30],
  ["processing_errors", 12],
  ["authorization_related_chargeback", 10],
  ["unknown", 6]
]

const COUNTRY_WEIGHTS = [
  ["US", 44],
  ["GB", 10],
  ["CA", 8],
  ["DE", 6],
  ["FR", 5],
  ["AU", 5],
  ["NL", 3],
  ["ES", 3],
  ["IT", 3],
  ["BR", 2],
  ["MX", 2],
  ["JP", 2],
  ["IN", 2],
  ["SE", 1],
  ["PL", 1],
  ["", 3]
]

const FIRST_NAMES = [
  "Olivia",
  "Liam",
  "Emma",
  "Noah",
  "Ava",
  "Ethan",
  "Sophia",
  "Mason",
  "Isabella",
  "Lucas",
  "Mia",
  "James",
  "Amelia",
  "Benjamin",
  "Harper",
  "Elijah",
  "Evelyn",
  "Henry",
  "Chloe",
  "Jack"
]

const LAST_NAMES = [
  "Smith",
  "Johnson",
  "Brown",
  "Garcia",
  "Miller",
  "Davis",
  "Martinez",
  "Wilson",
  "Anderson",
  "Taylor",
  "Thomas",
  "Moore",
  "Jackson",
  "White",
  "Harris",
  "Clark"
]

const TEAM = ["Mia Chen", "Daniel Ortiz", "Priya Nair", "Tom Becker"]

// mulberry32, kept to int32 so the state never loses precision.
const seeded = (seed) => {
  let state = seed
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), state | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const random = seeded(20260924)
const between = (min, max) => min + random() * (max - min)
const int = (min, max) => Math.floor(between(min, max + 1))
const oneOf = (list) => list[int(0, list.length - 1)]

// [[value, weight], ...] -> one value, drawn in proportion to its weight.
const pick = (options) => {
  const total = options.reduce((sum, [, weight]) => sum + weight, 0)
  let roll = random() * total
  for (const [value, weight] of options) {
    roll -= weight
    if (roll < 0) return value
  }
  return options[options.length - 1][0]
}

const round2 = (value) => Math.round(value * 100) / 100

// Older disputes have had time to be decided; an inquiry is never lost, it escalates.
const statusFor = (type, ageDays) => {
  if (type === "inquiry") {
    if (ageDays < 12)
      return pick([
        ["needs_response", 55],
        ["awaiting_review", 20],
        ["pending_decision", 25]
      ])
    if (ageDays < 40)
      return pick([
        ["pending_decision", 50],
        ["won", 30],
        ["escalated", 20]
      ])
    return pick([
      ["won", 65],
      ["escalated", 35]
    ])
  }
  if (ageDays < 20)
    return pick([
      ["needs_response", 45],
      ["awaiting_review", 22],
      ["pending_decision", 23],
      ["skipped", 10]
    ])
  if (ageDays < 75)
    return pick([
      ["pending_decision", 45],
      ["won", 20],
      ["lost_defended", 10],
      ["lost_undefended", 3],
      ["skipped", 9],
      ["awaiting_review", 7],
      ["needs_response", 6]
    ])
  return pick([
    ["won", 50],
    ["lost_defended", 22],
    ["lost_undefended", 6],
    ["skipped", 12],
    ["pending_decision", 10]
  ])
}

// The events the backend will read off its own tables (§11 of the contract). Offsets are hours
// after filing; a history longer than the time the dispute has had is squeezed into that time.
const historyOf = (dispute, now) => {
  const { id, status, dispute_type: type } = dispute
  const steps = []
  const step = (eventType, hours, eventId, params = null) =>
    steps.push({ event_type: eventType, hours, event_id: eventId, event_params: params })
  const outcomeAfter = (fromHours, minDays, maxDays) => fromHours + int(minDays * 24, maxDays * 24)

  if (status !== "needs_response") {
    step("generated", int(1, 6), `document-${id}-1`)
    if (random() < 0.25) step("regenerated", int(24, 72), `document-${id}-2`)
  }

  const reviewed =
    status === "awaiting_review" ||
    (type === "dispute" &&
      ["pending_decision", "won", "lost_defended"].includes(status) &&
      random() < 0.35)
  if (reviewed) step("review", int(6, 30), `transition-${id}-review`)

  let submittedAt = 0
  if (SUBMITTED.includes(status)) {
    submittedAt = reviewed ? int(40, 96) : int(24, 96)
    if (reviewed) step("approved", submittedAt, `submission-${id}`, { name: oneOf(TEAM) })
    else step("submitted_auto", submittedAt, `submission-${id}`)
    step("pending", submittedAt + 1, `transition-${id}-pending`)
  }

  if (status === "won") step("won", outcomeAfter(submittedAt, 20, 70), `transition-${id}-won`)
  if (status === "lost_defended")
    step("lost", outcomeAfter(submittedAt, 20, 70), `transition-${id}-lost`)
  if (status === "lost_undefended") step("lost", outcomeAfter(0, 18, 25), `transition-${id}-lost`)
  if (status === "escalated")
    step("escalated", outcomeAfter(submittedAt, 5, 20), `transition-${id}-escalated`)
  if (status === "skipped") step("not_fought", outcomeAfter(0, 1, 5), `transition-${id}-skipped`)

  const available = Math.max(now.diff(dispute.filed, "hour", true) - 0.5, 0.5)
  const last = Math.max(0, ...steps.map((s) => s.hours))
  const squeeze = last > available ? available / last : 1

  return [
    {
      event_id: `dispute-${id}-created`,
      event_type: "created",
      event_params: null,
      at: dispute.transaction
    },
    {
      event_id: `dispute-${id}-received`,
      event_type: "received",
      event_params: null,
      at: dispute.filed
    },
    ...steps.map(({ hours, ...event }) => ({
      ...event,
      at: dispute.filed.add(Math.round(hours * squeeze * 60), "minute")
    }))
  ]
}

const generate = () => {
  const now = dayjs()
  const customers = Array.from({ length: 700 }, (_, index) => {
    const first = oneOf(FIRST_NAMES)
    const last = oneOf(LAST_NAMES)
    return {
      name: `${first} ${last}`,
      email: `${first}.${last}.${index}@example.com`.toLowerCase()
    }
  })

  return Array.from({ length: DISPUTE_COUNT }, (_, index) => {
    const id = 5000 + index
    let filed = now
      .subtract(Math.floor(HISTORY_DAYS * random() ** 1.4), "day")
      .hour(int(0, 23))
      .minute(int(0, 59))
      .second(0)
    if (filed.isAfter(now)) filed = now.subtract(int(10, 300), "minute")

    const network = pick(NETWORK_WEIGHTS)
    // Only Amex and Discover raise inquiries.
    const type = ["amex", "discover"].includes(network) && random() < 0.35 ? "inquiry" : "dispute"
    const category = pick(CATEGORY_WEIGHTS)
    const codes = REASON_CODES[network]?.[category]
    const [code, description] = codes ? oneOf(codes) : ["", ""]
    // A small pool files a share of everything, so repeat disputers exist in any window.
    const customer = random() < 0.15 ? customers[int(0, 49)] : oneOf(customers)
    const processor = random() < 0.8 ? "stripe" : "shopify"
    const amount =
      random() < 0.05 ? between(900, 2500) : Math.exp(between(Math.log(15), Math.log(900)))

    const dispute = {
      id,
      dispute_type: type,
      status: statusFor(type, now.diff(filed, "day")),
      processor,
      provider_charge_id: processor === "stripe" ? `ch_3Q${id.toString(36)}${int(1e5, 1e6)}` : null,
      amount: round2(amount),
      category,
      network: NETWORK_LABELS[network] ? network : "other",
      network_reason_code: code,
      reason_description: description,
      customer_name: customer.name,
      customer_email: random() < 0.97 ? customer.email : null,
      card_country: pick(COUNTRY_WEIGHTS),
      filed,
      transaction: filed.subtract(int(3, 45), "day")
    }
    dispute.events = historyOf(dispute, now)
    return dispute
  })
}

let dataset = null
const allDisputes = () => {
  if (!dataset) dataset = generate()
  return dataset
}

/* ------------- Query helpers ------------- */

const csv = (value) =>
  value
    ? String(value)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : []

const frameOf = (start, end) =>
  start && end ? [dayjs(start).startOf("day"), dayjs(end).endOf("day")] : null

const inFrame = (at, frame) => !frame || (!at.isBefore(frame[0]) && !at.isAfter(frame[1]))

const byType = (list, params) => {
  const types = csv(params.dispute_type)
  return types.length ? list.filter((d) => types.includes(d.dispute_type)) : list
}

const scoped = (params, frame = frameOf(params.start_date, params.end_date)) =>
  byType(allDisputes(), params).filter((d) => inFrame(d.filed, frame))

const COMPARE_ID_OF = Object.fromEntries(
  Object.entries(COMPARE_BE_VALUES).map(([id, value]) => [value, id])
)

const comparisonPeriodOf = (params) => {
  if (!params.compare) return null
  const [from, to] =
    params.compare === "custom"
      ? [params.compare_start_date, params.compare_end_date]
      : getCompareRange([params.start_date, params.end_date], COMPARE_ID_OF[params.compare])
  if (!from || !to) return null
  return {
    start_date: from,
    end_date: to,
    type: params.compare,
    label: `${dayjs(from).format(LABEL_FORMAT)} – ${dayjs(to).format(LABEL_FORMAT)}`
  }
}

const comparedOf = (params, period) =>
  period ? scoped(params, frameOf(period.start_date, period.end_date)) : null

const groupsOf = (list, keyOf) => {
  const groups = new Map()
  for (const item of list) {
    const key = keyOf(item)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(item)
  }
  return groups
}

const sumAmount = (list) => round2(list.reduce((sum, d) => sum + d.amount, 0))

const winRateOf = (won, lost) => (won + lost ? round2((won / (won + lost)) * 100) : null)

const tally = (list) => {
  const won = list.filter((d) => d.status === "won").length
  const lost = list.filter((d) => LOST.includes(d.status)).length
  return {
    count: list.length,
    amount: sumAmount(list),
    won_count: won,
    lost_count: lost,
    resolved_count: won + lost,
    win_rate: winRateOf(won, lost)
  }
}

const shareOf = (part, total) => (total ? round2((part / total) * 100) : 0)

// Categories by count, "Other" always last whatever its size.
const byCategoryRank = (a, b) =>
  (a.category === "unknown") - (b.category === "unknown") || b.count - a.count

const paginate = (list, params, defaultSize) => {
  const page = Math.max(1, Number(params.page) || 1)
  const size = Number(params.page_size) || defaultSize
  return {
    count: list.length,
    next: page * size < list.length ? `?page=${page + 1}` : null,
    previous: page > 1 ? `?page=${page - 1}` : null,
    results: list.slice((page - 1) * size, page * size)
  }
}

/* ------------- Trend buckets ------------- */

const unitOf = (days) => (days <= 31 ? "day" : days <= 180 ? "week" : "month")

const stepOf = (unit) => (unit === "week" ? [7, "day"] : [1, unit])

const boundsOf = (params) => {
  if (params.start_date && params.end_date)
    return [dayjs(params.start_date), dayjs(params.end_date)]
  const first = allDisputes().reduce((min, d) => (d.filed.isBefore(min) ? d.filed : min), dayjs())
  return [first, dayjs()]
}

const timelineOf = ([from, to]) => {
  const unit = unitOf(to.diff(from, "day") + 1)
  const first = unit === "month" ? from.startOf("month") : from.startOf("day")
  const [amount, stepUnit] = stepOf(unit)
  const labels = []
  for (let cur = first; !cur.isAfter(to); cur = cur.add(amount, stepUnit)) {
    labels.push(cur.format(ISO))
  }
  return { unit, first, labels }
}

// The comparison window on the same unit and the same number of buckets, so the overlay lines up
// with the current window by index.
const comparisonTimelineOf = (timeline, period) => {
  const start = dayjs(period.start_date)
  const first = timeline.unit === "month" ? start.startOf("month") : start.startOf("day")
  const [amount, stepUnit] = stepOf(timeline.unit)
  return {
    ...timeline,
    first,
    labels: timeline.labels.map((_, index) => first.add(index * amount, stepUnit).format(ISO))
  }
}

const bucketIndex = (at, { unit, first }) => {
  if (unit === "month") return at.startOf("month").diff(first, "month")
  const days = at.startOf("day").diff(first, "day")
  return unit === "week" ? Math.floor(days / 7) : days
}

const bucketed = (list, timeline) => {
  const buckets = timeline.labels.map(() => [])
  for (const d of list) {
    const index = bucketIndex(d.filed, timeline)
    if (index >= 0 && index < buckets.length) buckets[index].push(d)
  }
  return buckets
}

const metricOf = (list, metric) => {
  switch (metric) {
    case "recovered":
      return sumAmount(list.filter((d) => d.status === "won"))
    case "at_risk":
      return sumAmount(list.filter((d) => ACTIVE.includes(d.status)))
    case "win_rate":
      return tally(list).win_rate
    case "avg_value":
      return list.length ? round2(sumAmount(list) / list.length) : 0
    default:
      return list.length
  }
}

/* ------------- Endpoints ------------- */

const kpiSummaryOf = (list) => {
  const { count, amount, won_count, lost_count, resolved_count, win_rate } = tally(list)
  const active = list.filter((d) => ACTIVE.includes(d.status))
  return {
    disputes_count: count,
    active_count: active.length,
    total_amount: amount,
    avg_amount: count ? round2(amount / count) : 0,
    recovered_amount: sumAmount(list.filter((d) => d.status === "won")),
    at_risk_amount: sumAmount(active),
    won_count,
    lost_count,
    resolved_count,
    win_rate
  }
}

export const getKpis = (params) => {
  const period = comparisonPeriodOf(params)
  const compared = comparedOf(params, period)
  return {
    comparison_period: period,
    summary: kpiSummaryOf(scoped(params)),
    comparison_summary: compared ? kpiSummaryOf(compared) : null
  }
}

export const getTrend = (params) => {
  const metric = params.metric || "disputes"
  const timeline = timelineOf(boundsOf(params))
  const period = comparisonPeriodOf(params)
  const comparisonTimeline = period ? comparisonTimelineOf(timeline, period) : null
  return {
    comparison_period: period,
    metric,
    unit: timeline.unit,
    labels: timeline.labels,
    data: bucketed(scoped(params), timeline).map((bucket) => metricOf(bucket, metric)),
    comparison: comparisonTimeline
      ? {
          labels: comparisonTimeline.labels,
          data: bucketed(comparedOf(params, period), comparisonTimeline).map((bucket) =>
            metricOf(bucket, metric)
          )
        }
      : null
  }
}

export const getFlow = (params) => {
  const groups = groupsOf(scoped(params), (d) => `${d.dispute_type}:${d.status}`)
  return {
    result: [...groups.values()].map((items) => ({
      dispute_type: items[0].dispute_type,
      status: items[0].status,
      count: items.length,
      amount: sumAmount(items)
    }))
  }
}

const categorySummaryOf = (list) => {
  const codes = [
    ...groupsOf(
      list.filter((d) => d.network_reason_code),
      (d) => d.network_reason_code
    )
  ]
  const top = codes.sort((a, b) => b[1].length - a[1].length)[0]
  return { ...tally(list), codes_count: codes.length, top_reason_code: top ? top[0] : null }
}

export const getByCategory = (params) => {
  const list = scoped(params)
  const period = comparisonPeriodOf(params)
  const compared = comparedOf(params, period)
  const result = [...groupsOf(list, (d) => d.category)]
    .map(([category, items]) => ({
      category,
      label: CATEGORY_LABELS[category],
      ...tally(items),
      share: shareOf(items.length, list.length),
      codes: [
        ...groupsOf(
          items.filter((d) => d.network_reason_code),
          (d) => d.network_reason_code
        )
      ]
        .map(([code, codeItems]) => ({
          reason_code: code,
          description: codeItems[0].reason_description,
          ...tally(codeItems),
          share: shareOf(codeItems.length, list.length)
        }))
        .sort((a, b) => b.count - a.count)
    }))
    .sort(byCategoryRank)

  return {
    comparison_period: period,
    result,
    summary: categorySummaryOf(list),
    comparison_summary: compared ? categorySummaryOf(compared) : null
  }
}

const countrySummaryOf = (list) => {
  const located = list.filter((d) => d.card_country)
  const countries = [...groupsOf(located, (d) => d.card_country)]
  const top = countries.sort((a, b) => b[1].length - a[1].length)[0]
  return {
    count: located.length,
    amount: sumAmount(located),
    countries_count: countries.length,
    top_country_code: top ? top[0] : null
  }
}

export const getByCountry = (params) => {
  const located = scoped(params).filter((d) => d.card_country)
  const period = comparisonPeriodOf(params)
  const compared = comparedOf(params, period)
  const result = [...groupsOf(located, (d) => d.card_country)]
    .map(([code, items]) => ({
      country_code: code,
      count: items.length,
      amount: sumAmount(items),
      avg_amount: round2(sumAmount(items) / items.length),
      share: shareOf(items.length, located.length)
    }))
    .sort((a, b) => b.count - a.count)

  return {
    comparison_period: period,
    result,
    summary: countrySummaryOf(located),
    comparison_summary: compared ? countrySummaryOf(compared) : null
  }
}

export const getByCardNetwork = (params) => {
  const list = scoped(params)
  const total = sumAmount(list)
  const result = [...groupsOf(list, (d) => d.network)]
    .map(([network, items]) => ({
      network,
      label: NETWORK_LABELS[network],
      ...tally(items),
      share: shareOf(sumAmount(items), total)
    }))
    .sort((a, b) => b.amount - a.amount)

  return { result }
}

export const getWinRateMatrix = (params) => {
  const rows = [...groupsOf(scoped(params), (d) => d.category)]
    .map(([category, items]) => ({
      category,
      label: CATEGORY_LABELS[category],
      count: items.length,
      cells: MATRIX_NETWORKS.map((network) => {
        const { won_count, resolved_count, win_rate } = tally(
          items.filter((d) => d.network === network)
        )
        return { network, won_count, resolved_count, win_rate }
      })
    }))
    .sort(byCategoryRank)

  return { networks: MATRIX_NETWORKS, rows }
}

const repeatRowsOf = (list) =>
  [
    ...groupsOf(
      list.filter((d) => d.customer_email),
      (d) => d.customer_email.trim().toLowerCase()
    )
  ]
    .filter(([, items]) => items.length > 1)
    .map(([email, items]) => {
      const topCategory = [...groupsOf(items, (d) => d.category)].sort(
        (a, b) => b[1].length - a[1].length
      )[0][0]
      const last = items.reduce((max, d) => (d.filed.isAfter(max) ? d.filed : max), items[0].filed)
      return {
        customer_email: email,
        customer_name: items[0].customer_name,
        ...tally(items),
        open_count: items.filter((d) => ACTIVE.includes(d.status)).length,
        top_category: topCategory,
        top_category_label: CATEGORY_LABELS[topCategory],
        last_dispute_at: last.toISOString()
      }
    })
    .sort((a, b) => b.count - a.count || b.amount - a.amount)
    .map((row, index) => ({ ...row, sequence_number: index + 1 }))

const repeatSummaryOf = (rows) => {
  const won = rows.reduce((sum, r) => sum + r.won_count, 0)
  const lost = rows.reduce((sum, r) => sum + r.lost_count, 0)
  return {
    customers_count: rows.length,
    disputes_count: rows.reduce((sum, r) => sum + r.count, 0),
    amount: round2(rows.reduce((sum, r) => sum + r.amount, 0)),
    won_count: won,
    resolved_count: won + lost,
    win_rate: winRateOf(won, lost)
  }
}

const REPEAT_ORDERING = ["count", "amount", "open_count", "win_rate", "last_dispute_at"]

// Nulls (no win rate yet) sort last in both directions; ties keep the rank order.
const orderRows = (rows, ordering) => {
  const field = ordering.replace(/^-/, "")
  if (!REPEAT_ORDERING.includes(field)) return rows
  const direction = ordering.startsWith("-") ? -1 : 1
  return [...rows].sort((a, b) => {
    if (a[field] == null || b[field] == null) {
      return (a[field] == null) - (b[field] == null) || a.sequence_number - b.sequence_number
    }
    const diff = a[field] < b[field] ? -1 : a[field] > b[field] ? 1 : 0
    return direction * diff || a.sequence_number - b.sequence_number
  })
}

export const getRepeatDisputers = (params) => {
  const rows = repeatRowsOf(scoped(params))
  const period = comparisonPeriodOf(params)
  const compared = comparedOf(params, period)
  const term = (params.search || "").trim().toLowerCase()
  const found = term
    ? rows.filter((r) => `${r.customer_name} ${r.customer_email}`.toLowerCase().includes(term))
    : rows

  return {
    comparison_period: period,
    ...paginate(orderRows(found, params.ordering || "-count"), params, 20),
    summary: repeatSummaryOf(rows),
    comparison_summary: compared ? repeatSummaryOf(repeatRowsOf(compared)) : null
  }
}

const DEFAULT_TREND_CODES = 8

export const getCategoryTrends = (params) => {
  const isCode = params.level === "code"
  const keyOf = isCode ? (d) => d.network_reason_code : (d) => d.category
  const list = scoped(params).filter(keyOf)
  const ranked = [...groupsOf(list, keyOf)]
    .map(([key, items]) => ({ key, category: isCode ? null : key, count: items.length }))
    .sort(isCode ? (a, b) => b.count - a.count : byCategoryRank)
    .map((row) => row.key)
  const wanted = csv(params.keys)
  const keys = wanted.length
    ? [
        ...ranked.filter((key) => wanted.includes(key)),
        ...wanted.filter((k) => !ranked.includes(k))
      ]
    : isCode
      ? ranked.slice(0, DEFAULT_TREND_CODES)
      : ranked
  const timeline = timelineOf(boundsOf(params))
  const buckets = bucketed(list, timeline)

  return {
    unit: timeline.unit,
    labels: timeline.labels,
    series: keys.map((key) => {
      const inSeries = buckets.map((bucket) => bucket.filter((d) => keyOf(d) === key))
      return {
        key,
        label: isCode ? key : CATEGORY_LABELS[key],
        count: inSeries.map((items) => items.length),
        amount: inSeries.map(sumAmount)
      }
    })
  }
}

const amountFilterOf = ({ amount_type: type, amount_min: min, amount_max: max }) => {
  const low = min == null || min === "" ? null : Number(min)
  const high = max == null || max === "" ? null : Number(max)
  return (amount) => {
    switch (type) {
      case "is_equal_to":
        return low == null || amount === low
      case "is_greater_than":
        return low == null || amount > low
      case "is_less_than":
        return low == null || amount < low
      case "is_between":
        return (low == null || amount >= low) && (high == null || amount <= high)
      default:
        return true
    }
  }
}

export const getActivity = (params) => {
  const frame = frameOf(params.start_date, params.end_date)
  const statuses = csv(params.status)
  const changes = csv(params.change)
  const categories = csv(params.reason_category)
  const terms = csv(params.customer).map((term) => term.toLowerCase())
  const amountMatches = amountFilterOf(params)

  const rows = []
  for (const d of byType(allDisputes(), params)) {
    if (statuses.length && !statuses.includes(d.status)) continue
    if (categories.length && !categories.includes(d.category)) continue
    if (!amountMatches(d.amount)) continue
    if (terms.length) {
      const haystack = `${d.customer_name} ${d.customer_email || ""}`.toLowerCase()
      if (!terms.some((term) => haystack.includes(term))) continue
    }
    for (const event of d.events) {
      if (changes.length && !changes.includes(event.event_type)) continue
      if (!inFrame(event.at, frame)) continue
      rows.push({
        event_id: event.event_id,
        event_type: event.event_type,
        event_params: event.event_params,
        event_time: event.at.toISOString(),
        dispute_id: d.id,
        dispute_type: d.dispute_type,
        status: d.status,
        status_label: STATUS_LABELS[d.status] || d.status,
        customer_name: d.customer_name,
        customer_email: d.customer_email,
        amount: d.amount,
        currency: "USD",
        category: d.category,
        category_label: CATEGORY_LABELS[d.category],
        reason_code: d.network_reason_code || null,
        reason_code_label: d.reason_description || null,
        processor: d.processor,
        provider_charge_id: d.provider_charge_id
      })
    }
  }
  rows.sort((a, b) => b.event_time.localeCompare(a.event_time))

  return paginate(rows, params, 50)
}
